import { fetchJson, apiUrl } from '../apiBase';

const previewRequests = new Map();

function runSingleFlight(key, requestFactory) {
  const existingRequest = previewRequests.get(key);
  if (existingRequest) return existingRequest;

  const request = Promise.resolve()
    .then(requestFactory)
    .finally(() => {
      if (previewRequests.get(key) === request) {
        previewRequests.delete(key);
      }
    });
  previewRequests.set(key, request);
  return request;
}

async function parseResponse(res, fallbackMessage) {
  let payload = null;
  try {
    payload = await res.json();
  } catch (_) {
    payload = null;
  }
  if (!res.ok) {
    const detail = payload?.detail || fallbackMessage;
    const error = new Error(typeof detail === 'string' ? detail : detail.message || fallbackMessage);
    error.status = res.status;
    error.detail = detail;
    throw error;
  }
  return payload;
}

export async function fetchBoard(token, boardSessionId) {
  const query = boardSessionId ? `?board_session_id=${encodeURIComponent(boardSessionId)}` : '';
  const res = await fetchJson(`/mini-tasks/board/${encodeURIComponent(token)}${query}`);
  return parseResponse(res, 'Mini Görev ekranı açılamadı.');
}

export async function pollTeacherMessage(token, boardSessionId) {
  const res = await fetchJson(`/mini-tasks/board/${encodeURIComponent(token)}/teacher-message/poll`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId }),
  });
  try {
    return await parseResponse(res, 'Öğretmen mesajı bağlantısı kurulamadı.');
  } catch (err) {
    err.status = res.status;
    throw err;
  }
}

// EventSource needs a URL (custom auth headers are unsupported). The existing
// class-link token is the same authorization capability used by HTTP polling.
// The board session ID selects the browser identity; server validates both.
export function openTeacherMessageEvents(token, boardSessionId) {
  const path = `/mini-tasks/board/${encodeURIComponent(token)}/teacher-message/events?board_session_id=${encodeURIComponent(boardSessionId)}`;
  return new EventSource(apiUrl(path));
}

export async function acknowledgeTeacherMessage(token, boardSessionId, messageId) {
  const response = await fetchJson(
    `/mini-tasks/board/${encodeURIComponent(token)}/teacher-message/ack`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ board_session_id: boardSessionId, message_id: messageId }) },
  );
  return parseResponse(response, 'Mesaj teslim onayı gönderilemedi.');
}

export async function fetchReadonlyStarGarden(token) {
  const res = await fetchJson(`/mini-tasks/board/${encodeURIComponent(token)}/star-garden`);
  return parseResponse(res, 'Yıldız Bahçem yüklenemedi.');
}

export async function fetchReadonlyLibrary(token) {
  const res = await fetchJson(`/mini-tasks/board/${encodeURIComponent(token)}/library`);
  return parseResponse(res, 'Kütüphanemiz yüklenemedi.');
}

export async function fetchBookPointEvents(token, after) {
  const suffix = after === null ? '' : `?after=${after}`;
  const res = await fetchJson(`/mini-tasks/board/${encodeURIComponent(token)}/library/point-events${suffix}`);
  if (!res.ok) throw new Error('Puan etkinlikleri yüklenemedi.');
  return res.json();
}

export async function fetchParentMiniTaskBootstrap() {
  const res = await fetchJson('/mini-tasks/parent/bootstrap');
  return parseResponse(res, 'Mini görev bilgileri alınamadı.');
}

export async function launchParentMiniTask(studentId, boardSessionId, options = {}) {
  if (options.readingAssignmentId) {
    const res = await fetchJson(`/parent/student360/reading/${encodeURIComponent(options.readingAssignmentId)}/launch?student_id=${encodeURIComponent(studentId)}`, { method: 'POST' });
    return parseResponse(res, 'Okuma başlatılamadı.');
  }

  const body = { student_id: String(studentId) };
  if (boardSessionId) body.board_session_id = String(boardSessionId);
  if (options.skipWeeklyReading) body.skip_weekly_reading = true;
  const res = await fetchJson('/mini-tasks/parent/launch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseResponse(res, 'Mini görev başlatılamadı.');
}

export async function startRound(token, studentId, boardSessionId) {
  const res = await fetchJson(`/mini-tasks/board/${encodeURIComponent(token)}/students/${encodeURIComponent(studentId)}/rounds`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId }),
  });
  return parseResponse(res, 'Mini görev başlatılamadı.');
}

export async function previewRound(token, studentId, boardSessionId, previewId, previewLevel, previewQuestionId = null) {
  const query = new URLSearchParams({
    preview_id: String(previewId),
  });
  if (previewLevel != null) query.set('preview_level', String(previewLevel));
  if (previewQuestionId) query.set('preview_question_id', String(previewQuestionId));
  const path = `/mini-tasks/board/${encodeURIComponent(token)}/students/${encodeURIComponent(studentId)}/preview-rounds?${query}`;
  const requestKey = `${path}|${String(boardSessionId)}`;

  return runSingleFlight(requestKey, async () => {
    const res = await fetchJson(
      path,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ board_session_id: boardSessionId }),
      },
    );
    return parseResponse(res, 'Preview görevi başlatılamadı.');
  });
}

export async function analyzeWeeklyReadingPreviewAudio(
  token,
  passageId,
  boardSessionId,
  audioBlob,
  audioDurationMs,
) {
  const body = new FormData();
  body.append('board_session_id', String(boardSessionId));
  body.append('passage_id', String(passageId));
  body.append('audio_duration_ms', String(audioDurationMs));
  body.append('audio', audioBlob, `weekly-reading-preview-${passageId}.webm`);
  const res = await fetchJson(
    `/mini-tasks/board/${encodeURIComponent(token)}/weekly-reading/preview-analysis`,
    { method: 'POST', body },
  );
  return parseResponse(res, 'Preview ses analizi tamamlanamadı.');
}

export async function startWeeklyReadingAttempt(studentAssignmentId, boardSessionId, idempotencyKey) {
  const res = await fetchJson(
    `/mini-tasks/weekly-reading/${encodeURIComponent(studentAssignmentId)}/attempts`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        board_session_id: boardSessionId,
        idempotency_key: idempotencyKey,
      }),
    },
  );
  return parseResponse(res, 'Okuma ölçümü başlatılamadı.');
}

export async function cancelWeeklyReadingAttempt(studentAssignmentId, attemptId, boardSessionId, reason = 'exit') {
  const res = await fetchJson(
    `/mini-tasks/weekly-reading/${encodeURIComponent(studentAssignmentId)}/attempts/${encodeURIComponent(attemptId)}/cancel`,
    {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ board_session_id: boardSessionId, reason }),
    },
  );
  return parseResponse(res, 'Okuma denemesi kapatılamadı.');
}

export async function completeWeeklyReadingAttempt(
  studentAssignmentId,
  attemptId,
  boardSessionId,
  idempotencyKey,
  measurement,
) {
  const res = await fetchJson(
    `/mini-tasks/weekly-reading/${encodeURIComponent(studentAssignmentId)}/attempts/${encodeURIComponent(attemptId)}/complete`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        board_session_id: boardSessionId,
        idempotency_key: idempotencyKey,
        client_elapsed_ms: Number(measurement.client_elapsed_ms),
        visibility_hidden_count: Number(measurement.visibility_hidden_count || 0),
        visibility_hidden_ms: Number(measurement.visibility_hidden_ms || 0),
        max_scroll_ratio: measurement.max_scroll_ratio == null
          ? null
          : Number(measurement.max_scroll_ratio),
      }),
    },
  );
  return parseResponse(res, 'Okuma sonucu kaydedilemedi.');
}

export async function uploadWeeklyReadingAudio(
  studentAssignmentId,
  attemptId,
  boardSessionId,
  audioBlob,
  audioDurationMs,
) {
  const body = new FormData();
  body.append('board_session_id', String(boardSessionId));
  body.append('privacy_notice_version', 'weekly-reading-audio-privacy-v3');
  body.append('audio_duration_ms', String(audioDurationMs));
  body.append('audio', audioBlob, `weekly-reading-${attemptId}.webm`);
  const res = await fetchJson(
    `/mini-tasks/weekly-reading/${encodeURIComponent(studentAssignmentId)}/attempts/${encodeURIComponent(attemptId)}/audio`,
    { method: 'POST', body },
  );
  return parseResponse(res, 'Ses kaydı yüklenemedi.');
}

export async function fetchWeeklyReadingAnalysis(studentAssignmentId, attemptId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(
    `/mini-tasks/weekly-reading/${encodeURIComponent(studentAssignmentId)}/attempts/${encodeURIComponent(attemptId)}/analysis${query}`,
    { dedupe: false },
  );
  return parseResponse(res, 'Okuma analizi alınamadı.');
}

export async function completeRound(roundId, boardSessionId, answers, finishReason = 'completed') {
  const res = await fetchJson(`/mini-tasks/rounds/${encodeURIComponent(roundId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      board_session_id: boardSessionId,
      finish_reason: finishReason,
      answers: answers.map((answer) => ({
        question_id: Number(answer.question_id),
        selected_answer: String(answer.selected_answer),
      })),
    }),
  });
  return parseResponse(res, 'Tur sonucu kaydedilemedi.');
}

export async function recordMathPresentationAnswer(token, snapshotId, boardSessionId, questionId, selectedAnswer) {
  const res = await fetchJson(`/mini-tasks/board/${encodeURIComponent(token)}/presentation-previews/${encodeURIComponent(snapshotId)}/answers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId, question_id: String(questionId),
      ...(Array.isArray(selectedAnswer?.selectedTokenIds) ? { selectedTokenIds: selectedAnswer.selectedTokenIds } : { selected_answer: String(selectedAnswer) }) }),
  });
  return parseResponse(res, 'Önizleme cevabı kaydedilemedi.');
}

export async function beginPreparedRound(roundId, boardSessionId) {
  const res = await fetchJson(`/mini-tasks/rounds/${encodeURIComponent(roundId)}/begin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId }),
  });
  return parseResponse(res, 'Mini görev başlatılamadı.');
}

export async function completeMemoryRound(roundId, boardSessionId, attemptCounts, finishReason = 'completed', matchedCounts = null) {
  const res = await fetchJson(`/mini-tasks/memory-rounds/${encodeURIComponent(roundId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      board_session_id: boardSessionId,
      attempt_counts: attemptCounts.map(Number),
      finish_reason: finishReason,
      ...(matchedCounts ? { matched_counts: matchedCounts.map(Number) } : {}),
    }),
  });
  return parseResponse(res, 'Hafıza görevi kaydedilemedi.');
}

export async function recoverMemoryRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/memory-rounds/${encodeURIComponent(roundId)}${query}`, {
    dedupe: false,
  });
  return parseResponse(res, 'Hafıza görevi geri yüklenemedi.');
}

export async function completeWordBuildRound(roundId, boardSessionId, attempts) {
  const res = await fetchJson(`/mini-tasks/word-build-rounds/${encodeURIComponent(roundId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId, attempts }),
  });
  return parseResponse(res, 'Kelimeyi Kur sonucu kaydedilemedi.');
}

export async function timeoutWordBuildRound(roundId, boardSessionId, attempts = []) {
  const res = await fetchJson(`/mini-tasks/word-build-rounds/${encodeURIComponent(roundId)}/timeout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId, attempts }),
  });
  return parseResponse(res, 'Kelimeyi Kur sonucu kaydedilemedi.');
}

export async function recoverWordBuildRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/word-build-rounds/${encodeURIComponent(roundId)}${query}`, {
    dedupe: false,
  });
  return parseResponse(res, 'Kelimeyi Kur görevi geri yüklenemedi.');
}

export async function completeReflexRound(roundId, boardSessionId, score, interactionCount) {
  const res = await fetchJson(`/mini-tasks/reflex-rounds/${encodeURIComponent(roundId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      board_session_id: boardSessionId,
      score: Number(score),
      ...(interactionCount == null ? {} : { interaction_count: Number(interactionCount) }),
    }),
  });
  return parseResponse(res, 'Refleks görevi kaydedilemedi.');
}

export async function completeDigitRecallRound(roundId, boardSessionId, responses, finishReason = 'completed') {
  const res = await fetchJson(`/mini-tasks/digit-recall-rounds/${encodeURIComponent(roundId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      board_session_id: boardSessionId,
      responses: responses.map(String),
      finish_reason: finishReason,
    }),
  });
  return parseResponse(res, 'Numara hatırlama görevi kaydedilemedi.');
}

export async function recoverDigitRecallRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/digit-recall-rounds/${encodeURIComponent(roundId)}${query}`, {
    dedupe: false,
  });
  return parseResponse(res, 'Numara hatırlama görevi geri yüklenemedi.');
}

export async function startOddOneOutPlay(roundId, boardSessionId) {
  const res = await fetchJson(`/mini-tasks/odd-one-out-rounds/${encodeURIComponent(roundId)}/play`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId }),
  });
  return parseResponse(res, 'Farklı olanı bul oyunu başlatılamadı.');
}

export async function completeOddOneOutRound(roundId, boardSessionId, events, finishReason = 'timer') {
  const res = await fetchJson(`/mini-tasks/odd-one-out-rounds/${encodeURIComponent(roundId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      board_session_id: boardSessionId,
      finish_reason: finishReason,
      events: events.map((event) => ({
        board_id: String(event.board_id),
        item_id: String(event.item_id),
        elapsed_ms: Number(event.elapsed_ms),
      })),
    }),
  });
  return parseResponse(res, 'Farklı olanı bul sonucu kaydedilemedi.');
}

export async function recoverOddOneOutRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/odd-one-out-rounds/${encodeURIComponent(roundId)}${query}`, {
    dedupe: false,
  });
  return parseResponse(res, 'Farklı olanı bul görevi geri yüklenemedi.');
}

export async function saveSoundHuntProgress(roundId, boardSessionId, events) {
  const res = await fetchJson(`/mini-tasks/sound-hunt-rounds/${encodeURIComponent(roundId)}/progress`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId, events }),
  });
  return parseResponse(res, 'Ses avı ilerlemesi kaydedilemedi. Yeniden deneyelim.');
}

export async function completeTurkishL1Tour(roundId, boardSessionId, submission) {
  const res = await fetchJson(`/mini-tasks/turkish-l1-tours/${encodeURIComponent(roundId)}/complete`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...submission, board_session_id: boardSessionId }),
  });
  return parseResponse(res, 'Tur kaydedilemedi. Cevapların burada duruyor; yeniden deneyelim.');
}

export async function recoverSoundHuntRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/sound-hunt-rounds/${encodeURIComponent(roundId)}${query}`, { dedupe: false });
  return parseResponse(res, 'Ses avı turu yüklenemedi. Yeniden deneyelim.');
}

export async function saveTargetSoundHuntProgress(roundId, boardSessionId, events) {
  const res = await fetchJson(`/mini-tasks/target-sound-hunt-rounds/${encodeURIComponent(roundId)}/progress`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId, events }),
  });
  return parseResponse(res, 'Ses bulma ilerlemesi kaydedilemedi. Yeniden deneyelim.');
}

export async function recoverTargetSoundHuntRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/target-sound-hunt-rounds/${encodeURIComponent(roundId)}${query}`, { dedupe: false });
  return parseResponse(res, 'Ses bulma turu yüklenemedi. Yeniden deneyelim.');
}

export async function saveSoundDiscoveryProgress(roundId, boardSessionId, events) {
  const res = await fetchJson(`/mini-tasks/sound-discovery-rounds/${encodeURIComponent(roundId)}/progress`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId, events }),
  });
  return parseResponse(res, 'Ses keşfi ilerlemesi kaydedilemedi. Yeniden deneyelim.');
}

export async function recoverSoundDiscoveryRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/sound-discovery-rounds/${encodeURIComponent(roundId)}${query}`, { dedupe: false });
  return parseResponse(res, 'Ses keşfi turu yüklenemedi. Yeniden deneyelim.');
}

export async function saveLetterHuntProgress(roundId, boardSessionId, events) {
  const res = await fetchJson(`/mini-tasks/letter-hunt-rounds/${encodeURIComponent(roundId)}/progress`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId, events }),
  });
  return parseResponse(res, 'Harf avı ilerlemesi kaydedilemedi. Yeniden deneyelim.');
}

export async function recoverLetterHuntRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/letter-hunt-rounds/${encodeURIComponent(roundId)}${query}`, { dedupe: false });
  return parseResponse(res, 'Harf avı turu yüklenemedi. Yeniden deneyelim.');
}

export async function completeCargoSortingRound(roundId, boardSessionId, counts) {
  const res = await fetchJson(`/mini-tasks/cargo-sorting-rounds/${encodeURIComponent(roundId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      board_session_id: boardSessionId,
      shown_count: Number(counts.shown_count),
      correct_count: Number(counts.correct_count),
      wrong_count: Number(counts.wrong_count),
      missed_count: Number(counts.missed_count),
    }),
  });
  return parseResponse(res, 'Kargo ayrıştırma sonucu kaydedilemedi.');
}

export async function recoverCargoSortingRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/cargo-sorting-rounds/${encodeURIComponent(roundId)}${query}`, {
    dedupe: false,
  });
  return parseResponse(res, 'Kargo ayrıştırma görevi geri yüklenemedi.');
}

export async function completePatternRecognitionRound(roundId, boardSessionId, rounds) {
  const res = await fetchJson(`/mini-tasks/pattern-recognition-rounds/${encodeURIComponent(roundId)}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board_session_id: boardSessionId, rounds }),
  });
  return parseResponse(res, 'Deseni Yakala sonucu kaydedilemedi.');
}

export async function recoverPatternRecognitionRound(roundId, boardSessionId) {
  const query = `?board_session_id=${encodeURIComponent(boardSessionId)}`;
  const res = await fetchJson(`/mini-tasks/pattern-recognition-rounds/${encodeURIComponent(roundId)}${query}`, {
    dedupe: false,
  });
  return parseResponse(res, 'Deseni Yakala görevi geri yüklenemedi.');
}
